/**
 * ResizableSplit — two panels divided by a draggable vertical handle: the
 * power-user master/detail where the reader sets the ratio (the fixed
 * `SplitView` is its non-resizable sibling). Fills its parent height; each side
 * scrolls on its own.
 *
 * `defaultRatio` is the left fraction (0–1); `min` is the px floor per side.
 * Drive it by pointer drag, ←/→ to nudge the focused divider, or double-click
 * to reset to `defaultRatio`.
 */
import { type JSX, createSignal } from "solid-js";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

export function ResizableSplit(props: {
  left: JSX.Element;
  right: JSX.Element;
  defaultRatio?: number;
  min?: number;
  class?: string;
}): JSX.Element {
  let host: HTMLDivElement | undefined;
  const [ratio, setRatio] = createSignal(props.defaultRatio ?? 0.5);
  const [dragging, setDragging] = createSignal(false);
  const min = () => props.min ?? 160;

  const clampRatio = (r: number): number => {
    if (!host) return Math.min(0.9, Math.max(0.1, r));
    const w = host.getBoundingClientRect().width;
    const lo = min() / w;
    const hi = 1 - min() / w;
    return Math.min(hi, Math.max(lo, r));
  };
  const fromX = (clientX: number): void => {
    if (!host) return;
    const r = host.getBoundingClientRect();
    setRatio(clampRatio((clientX - r.left) / r.width));
  };
  const onPointerDown = (e: PointerEvent): void => {
    e.preventDefault();
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent): void => fromX(ev.clientX);
    const up = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setRatio((r) => clampRatio(r - 0.02));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setRatio((r) => clampRatio(r + 0.02));
    }
  };

  return (
    <div
      ref={host}
      class={cn(
        "flex min-h-0 w-full overflow-hidden rounded-large border",
        "border-[color:var(--c9-border)] bg-[var(--c9-surface)]",
        props.class,
      )}
    >
      <div class="min-h-0 min-w-0 overflow-auto" style={{ width: `${ratio() * 100}%` }}>
        {props.left}
      </div>
      {/* Draggable divider */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panels"
        tabindex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKey}
        onDblClick={() => setRatio(props.defaultRatio ?? 0.5)}
        class={cn(
          "group relative flex w-px shrink-0 cursor-col-resize items-center justify-center",
          "bg-[color:var(--c9-border)]",
          focusRing,
        )}
      >
        {/* wider invisible hit area */}
        <span aria-hidden="true" class="absolute inset-y-0 -left-[5px] -right-[5px]" />
        {/* grab handle */}
        <span
          aria-hidden="true"
          class={cn(
            "relative h-8 w-[3px] rounded-full transition-colors",
            dragging()
              ? "bg-[var(--c9-border-selected)]"
              : "bg-[color:var(--c9-border-bold)] group-hover:bg-[var(--c9-border-selected)]",
          )}
        />
      </div>
      <div class="min-h-0 min-w-0 flex-1 overflow-auto">{props.right}</div>
    </div>
  );
}
